import { BaseService } from '../base';
import { filenameFromResponse } from '../../shared/utils/download-blob.util';
import { firstNameSegment } from '../../shared/utils/first-name-segment.util';
import type {
  GetPoliciesParams,
  InstallmentItem,
  PoliciesApiResponse,
  PoliciesResponse,
  PolicyApiItem,
  PolicyDetailsResponse,
  PolicySummary,
} from './types';

const DEFAULT_PAGE_SIZE = 10;
const PORTAL_API_URL = import.meta.env.VITE_API_URL as string | undefined;

function mapApiItemToSummary(item: PolicyApiItem): PolicySummary {
  return {
    id: item.policyId || '',
    insuredName: firstNameSegment(item.insuredName) || '-',
    insuredDocument: firstNameSegment(item.insuredDocument) || '',
    brokerName: item.brokerName || '-',
    brokerDocument: item.brokerDocument || '',
    inclusionUserName:
      item.inclusionUserName?.trim() || item.insertByUserName?.trim() || '-',
    productName: item.productName || '-',
    productCode: item.productCode,
    policyNo: item.policyNo || '-',
    proposalNo: item.proposalNo || '-',
    policyStatus: item.policyStatus ?? 0,
    statusPortal: item.statusPortal ?? 0,
    effectiveDate: item.effectiveDate || '',
    expiryDate: item.expiryDate || '',
    externalPolicyNo: item.externalPolicyNo != null ? String(item.externalPolicyNo) : '-',
    duePremium: Number(item.duePremium) || 0,
  };
}

export class PoliciesService extends BaseService {
  constructor() {
    super();
  }

  async getPolicies(params: GetPoliciesParams = {}): Promise<{
    success: boolean;
    data?: PoliciesResponse;
    error?: string;
  }> {
    const {
      pageNumber = 1,
      pageSize = DEFAULT_PAGE_SIZE,
      search = '',
      sortField = 'ExternalEffectiveDate',
      sortType = 'desc',
      status = '',
      productCode = '',
      partyCode,
      partyId,
      advisoryId,
      effectiveDateFrom,
      effectiveDateTo,
    } = params;

    try {
      const queryParams = new URLSearchParams({
        pageNumber: String(pageNumber),
        pageSize: String(pageSize),
        sortField,
        sortType,
      });

      if (search) queryParams.append('filter', search);
      if (status) queryParams.append('status', status);
      if (productCode) queryParams.append('productCode', productCode);
      if (partyCode) queryParams.append('partyCode', partyCode);
      if (partyId) queryParams.append('partyId', partyId);
      if (advisoryId) queryParams.append('advisoryId', advisoryId);
      if (effectiveDateFrom)
        queryParams.append('effectiveDateFrom', effectiveDateFrom);
      if (effectiveDateTo)
        queryParams.append('effectiveDateTo', effectiveDateTo);

      const path = `/portal/v1/emissions/policies?${queryParams.toString()}`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      const json =
        await this.handleJsonResponse<PoliciesApiResponse>(response);

      if (!json.success || !json.data) {
        return {
          success: false,
          error: json.error || 'Erro ao carregar apólices',
        };
      }

      return {
        success: true,
        data: {
          data: (json.data.data || []).map(mapApiItemToSummary),
          meta: json.data.meta,
        },
      };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  /**
   * @param subStipulatorNumber desempata parcelas de mesmo número (Capital
   *   Global/Vida PME: cada subestipulante pode ter a sua parcela 1).
   */
  async getInstallmentPDF(
    policyId: string,
    installmentNumber: number,
    subStipulatorNumber?: number | null,
  ): Promise<{
    success: boolean;
    data?: { number: number; pdfBase64: string }[];
    error?: string;
  }> {
    try {
      const queryParams = new URLSearchParams({
        policyId,
        installmentNumber: String(installmentNumber),
      });
      if (subStipulatorNumber != null) {
        queryParams.set('subStipulatorNumber', String(subStipulatorNumber));
      }
      const path = `/portal/v1/installments?${queryParams.toString()}`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      const json = await this.handleJsonResponse<
        { number: number; pdfBase64: string }[] | { number: number; pdfBase64: string }
      >(response);

      if (json.success && json.data && !Array.isArray(json.data)) {
        json.data = [json.data] as any;
      }

      return json as { success: boolean; data?: { number: number; pdfBase64: string }[]; error?: string };
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  async getInstallments(policyId: string): Promise<{
    success: boolean;
    data?: InstallmentItem[];
    error?: string;
  }> {
    try {
      const encodedPolicyId = encodeURIComponent(policyId);
      const path = `/portal/v1/installments/${encodedPolicyId}`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return this.handleJsonResponse<InstallmentItem[]>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }

  /**
   * Erro de uma rota que devolve binário (PDF/kit) ou vazio: lê o corpo uma
   * vez só, usa o `detail` quando houver e dispara o toast central de
   * corretora inativa (403) — esses caminhos não passam pelo
   * `handleJsonResponse`.
   */
  private async errorFromResponse(
    response: Response,
    fallback: string
  ): Promise<Error> {
    let detail = '';
    try {
      const body = (await response.json()) as { detail?: unknown };
      if (typeof body?.detail === 'string') detail = body.detail.trim();
    } catch {
      // corpo vazio ou não-JSON: fica o texto padrão
    }
    this.notifyInactiveBroker(response.status, detail);
    return new Error(detail || fallback);
  }

  async getPolicyPdf(policyId: string): Promise<Blob> {
    const path = `/portal/v1/emissions/policies/${encodeURIComponent(policyId)}/policy-pdf`;
    const response = await this.get(path, {}, PORTAL_API_URL);
    if (!response.ok) {
      throw await this.errorFromResponse(
        response,
        `Erro ao baixar PDF: ${response.status}`
      );
    }
    return response.blob();
  }

  async sendPolicyEmail(policyId: string, email: string): Promise<void> {
    const path = `/portal/v1/emissions/policies/${encodeURIComponent(policyId)}/send-email`;
    const response = await this.post(path, { email }, {}, PORTAL_API_URL);
    if (!response.ok) {
      throw await this.errorFromResponse(
        response,
        `Erro ao enviar e-mail: ${response.status}`
      );
    }
  }

  async getStarterKit(policyId: string): Promise<{ blob: Blob; filename: string }> {
    const path = `/portal/v1/emissions/policies/${encodeURIComponent(policyId)}/starter-kit`;
    const response = await this.get(path, {}, PORTAL_API_URL);
    if (!response.ok) {
      throw await this.errorFromResponse(
        response,
        `Erro ao baixar Kit: ${response.status}`
      );
    }
    const blob = await response.blob();
    const filename = filenameFromResponse(response, `kit-apolice-${policyId}.zip`);
    return { blob, filename };
  }

  async getPolicyDetails(policyId: string): Promise<{
    success: boolean;
    data?: PolicyDetailsResponse;
    error?: string;
  }> {
    try {
      const path = `/portal/v1/emissions/policies/${policyId}`;
      const response = await this.get(path, {}, PORTAL_API_URL);
      return this.handleJsonResponse<PolicyDetailsResponse>(response);
    } catch (error) {
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Erro de conexão',
      };
    }
  }
}

export const policiesService = new PoliciesService();
